import { useId } from "react";
import { displayValue, isDanglingReference, widgetFor } from "./params";
import type { ParamSpec } from "./types";

/**
 * Editor for one rule's parameter bundle.
 *
 * One control per param the action declares, chosen by the declared type. Errors arrive keyed by
 * param name — the API returns them nested under extra_params, flattened to dotted paths by
 * api.ts — so each message lands on the input that caused it rather than in a box at the top.
 */
export function ParamsEditor({
    specs,
    values,
    errors,
    disabled,
    onChange,
}: {
    specs: ParamSpec[];
    values: Record<string, unknown>;
    errors: Record<string, string[]>;
    disabled?: boolean;
    onChange: (name: string, value: unknown) => void;
}) {
    const idPrefix = useId();

    if (!specs.length) {
        return (
            <p className="aer-wf-params--empty">
                This action declares no parameters.
            </p>
        );
    }

    return (
        <div className="aer-wf-fields">
            {specs.map((spec) => {
                const widget = widgetFor(spec);
                const fieldId = `${idPrefix}-${spec.name}`;
                const fieldErrors = errors[`extra_params.${spec.name}`] ?? [];
                const dangling = isDanglingReference(spec, values[spec.name]);
                const describedBy = fieldErrors.length
                    ? `${fieldId}-error`
                    : dangling
                      ? `${fieldId}-dangling`
                      : undefined;

                return (
                    <div className="aer-wf-field" key={spec.name}>
                        <label htmlFor={fieldId}>
                            {spec.name}
                            {spec.deprecated && (
                                <span className="aer-wf-badge aer-wf-badge--warn">
                                    deprecated
                                </span>
                            )}
                        </label>

                        {widget === "checkbox" && (
                            <input
                                id={fieldId}
                                type="checkbox"
                                disabled={disabled}
                                checked={
                                    displayValue(spec, values[spec.name]) ===
                                    true
                                }
                                aria-describedby={describedBy}
                                onChange={(event) =>
                                    onChange(spec.name, event.target.checked)
                                }
                            />
                        )}

                        {widget === "picker" && (
                            <select
                                id={fieldId}
                                disabled={disabled}
                                value={String(
                                    displayValue(spec, values[spec.name]),
                                )}
                                aria-describedby={describedBy}
                                onChange={(event) =>
                                    onChange(spec.name, event.target.value)
                                }
                            >
                                {/* Clearing is a real choice: it restores the action's own
                                    fallback rather than leaving a stale reference. */}
                                <option value="">— not set —</option>
                                {dangling && (
                                    <option
                                        value={String(values[spec.name])}
                                        disabled
                                    >
                                        {String(values[spec.name])} — not
                                        available to this company
                                    </option>
                                )}
                                {spec.choices.map((choice) => (
                                    <option
                                        key={choice.value}
                                        value={choice.value}
                                    >
                                        {choice.label}
                                    </option>
                                ))}
                            </select>
                        )}

                        {widget === "text" && (
                            <input
                                id={fieldId}
                                type="text"
                                disabled={disabled}
                                value={String(
                                    displayValue(spec, values[spec.name]),
                                )}
                                aria-describedby={describedBy}
                                onChange={(event) =>
                                    onChange(spec.name, event.target.value)
                                }
                            />
                        )}

                        {dangling && !fieldErrors.length && (
                            <p
                                className="aer-wf-field__hint aer-wf-field__hint--warn"
                                id={`${fieldId}-dangling`}
                            >
                                The stored value is not available to this
                                company. The action will log and do nothing.
                            </p>
                        )}

                        {fieldErrors.length > 0 && (
                            <p
                                className="aer-wf-field__error"
                                id={`${fieldId}-error`}
                                role="alert"
                            >
                                {fieldErrors.join(" ")}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
